/**
 * AccountsPage — CRM account list (warm-ivory tokens).
 *
 * Apr 24 2026 — retokenised + modal extracted. The "+ New Account" CTA now
 * opens `CreateAccountModal` (new file) with a Type selector (customer /
 * vendor / partner / prospect). The old inline modal was removed.
 *
 * Role gate: SALES_EXECUTIVE and above can see the CTA. Everyone else sees
 * the list in read-only mode.
 *
 * UI-only · wires to existing POST /accounts and GET /accounts.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, Plus, Filter, X, Building2, Loader2, ChevronDown, ChevronUp, Pencil, Trash2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { resolveRoleKey } from '@/pages/dashboard/roleLayouts';
import { CreateAccountModal } from './CreateAccountModal';

interface Account {
  id: string;
  name: string;
  type: string;
  industry?: string;
  phone?: string;
  email?: string;
  gstin?: string;
  pan?: string;
  stateCode?: string;
  billingAddress?: string;
  createdAt: string;
  _count?: { contacts?: number; deals?: number };
}

const TYPES = ['customer', 'vendor', 'partner', 'prospect'];

const TYPE_CHIP: Record<string, string> = {
  customer: 'chip-green',
  vendor:   'chip-amber',
  partner:  'chip-blue',
  prospect: 'chip',
};

export function AccountsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const roleKey = useMemo(() => resolveRoleKey(user as never), [user]);
  const canCreate =
    roleKey === 'ADMIN' ||
    roleKey === 'SALES_MANAGER' ||
    roleKey === 'SALES_EXECUTIVE';

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [createOpen, setCreateOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {
        page: String(page),
        limit: String(limit),
      };
      if (search) params.search = search;
      if (filterType) params.type = filterType;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortOrder = sortDir;
      const res = await api.get('/accounts', { params });
      setAccounts(res.data.data?.accounts ?? res.data.data ?? []);
      setTotal(res.data.data?.pagination?.total ?? res.data.pagination?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [search, filterType, sortField, sortDir, page]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const handleCreated = (account: any) => {
    if (account?.id) {
      setAccounts((prev) => [account, ...prev]);
      setTotal((t) => t + 1);
    }
    setToast(`Account “${account?.name ?? 'new'}” created`);
    window.setTimeout(() => setToast(null), 3200);
    fetchAccounts();
  };

  const handleDelete = async (id: string) => {
    if (!canCreate) return;
    if (!confirm('Are you sure you want to delete this account?')) return;
    try {
      await api.delete(`/accounts/${id}`);
      fetchAccounts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete account');
    }
  };

  return (
    <div
      className="aura-root"
      style={{ padding: 24, background: 'var(--ivory-0)', minHeight: '100%' }}
    >
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1
            style={{
              fontFamily: 'var(--ff-serif)',
              fontSize: 28,
              fontWeight: 500,
              letterSpacing: -0.02,
              color: 'var(--ink-0)',
              marginBottom: 2,
            }}
          >
            Accounts
          </h1>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            Manage your business accounts
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setCreateOpen(true)}
            className="btn btn-primary"
            style={{ height: 36, padding: '0 14px' }}
          >
            <Plus size={14} />
            New Account
          </button>
        )}
      </div>

      {/* Search + Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1" style={{ maxWidth: 380 }}>
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-3)' }} />
          <input
            type="text"
            placeholder="Search accounts…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="ivory-input"
            style={{ paddingLeft: 32 }}
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={showFilters ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
        >
          <Filter size={13} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div
          className="card mb-4"
          style={{ padding: 16, display: 'flex', flexWrap: 'wrap', gap: 12 }}
        >
          <select
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
            className="ivory-input"
            style={{ width: 'auto', minWidth: 160 }}
          >
            <option value="">All types</option>
            {TYPES.map(t => (
              <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
          {filterType && (
            <button
              onClick={() => { setFilterType(''); setPage(1); }}
              className="btn btn-outline btn-sm"
            >
              <X size={12} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="card" style={{ overflow: 'hidden', padding: 0 }}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={20} className="animate-spin" style={{ color: 'var(--saffron-600)' }} />
            <span className="ml-2 text-sm" style={{ color: 'var(--ink-3)' }}>Loading accounts…</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20" style={{ color: 'var(--danger)' }}>
            <p className="text-sm">{error}</p>
            <button onClick={fetchAccounts} className="mt-2 btn btn-outline btn-sm">Retry</button>
          </div>
        ) : accounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20" style={{ color: 'var(--ink-3)' }}>
            <Building2 size={36} className="mb-2" />
            <p className="text-sm">No accounts yet</p>
            <p className="text-xs">Add your first account to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                    <span className="row gap-1 center">Name <SortIcon field="name" /></span>
                  </th>
                  <th>Type</th>
                  <th>Industry</th>
                  <th>Phone</th>
                  <th>GSTIN</th>
                  <th style={{ textAlign: 'center' }}>Contacts</th>
                  <th style={{ textAlign: 'center' }}>Deals</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.id} onClick={() => navigate(`/accounts/${a.id}`)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontWeight: 500, color: 'var(--ink-0)' }}>{a.name}</td>
                    <td>
                      <span className={`chip ${TYPE_CHIP[a.type] || 'chip'}`}>
                        {a.type.charAt(0).toUpperCase() + a.type.slice(1)}
                      </span>
                    </td>
                    <td style={{ color: 'var(--ink-2)' }}>{a.industry || '-'}</td>
                    <td style={{ color: 'var(--ink-2)' }}>{a.phone || '-'}</td>
                    <td className="mono text-xs" style={{ color: 'var(--ink-2)' }}>{a.gstin || '-'}</td>
                    <td style={{ textAlign: 'center', color: 'var(--ink-2)' }}>{a._count?.contacts ?? 0}</td>
                    <td style={{ textAlign: 'center', color: 'var(--ink-2)' }}>{a._count?.deals ?? 0}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/accounts/${a.id}`); }}
                          className="btn btn-ghost btn-sm"
                          style={{ padding: 4 }}
                          title="View / edit"
                        >
                          <Pencil size={13} />
                        </button>
                        {canCreate && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(a.id); }}
                            className="btn btn-ghost btn-sm"
                            style={{ padding: 4, color: 'var(--danger)' }}
                            title="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <div
            className="row between"
            style={{ padding: '12px 16px', borderTop: '1px solid var(--line)' }}
          >
            <div className="text-sm" style={{ color: 'var(--ink-3)' }}>
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
            </div>
            <div className="row gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn btn-outline btn-sm"
              >
                Previous
              </button>
              <span className="text-sm" style={{ color: 'var(--ink-3)' }}>Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn btn-outline btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div
          className="fixed"
          style={{
            bottom: 24,
            right: 24,
            background: 'var(--ink-0)',
            color: 'var(--ivory-0)',
            padding: '10px 16px',
            borderRadius: 'var(--r-sm)',
            boxShadow: 'var(--sh-3)',
            fontSize: 13,
            zIndex: 60,
          }}
        >
          {toast}
        </div>
      )}

      {/* Create modal */}
      <CreateAccountModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      <style>{`
        .aura-root .ivory-input {
          width: 100%;
          padding: 8px 10px;
          font-size: var(--fs-sm);
          font-family: inherit;
          color: var(--ink-0);
          background: var(--ivory-0);
          border: 1px solid var(--line);
          border-radius: var(--r-sm);
          outline: none;
          transition: border-color .12s, box-shadow .12s;
        }
        .aura-root .ivory-input:focus {
          border-color: var(--saffron-600);
          box-shadow: 0 0 0 3px var(--saffron-50);
        }
      `}</style>
    </div>
  );
}
